import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Download, FileSpreadsheet, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";

/**
 * A spreadsheet import: choose → check → import. The flow both imports share.
 *
 * Written for employees first (Shakun HR's first attempt, 14 Sep 2026, was refused ten times
 * by a toast naming a column that was in neither their file nor the dialog), then given to
 * trainings on 4 Nov 2026 — so HR meets one flow twice, and a fix to it reaches both. What
 * differs between them is the report: which decisions a file needs and how a ready row
 * looks. That is `renderReport`; everything around it lives here.
 *
 * Nothing is written until every row passes. The column guide comes from the server, which
 * is the same list the import reads — a guide written here would be one more copy of a
 * vocabulary that already drifted with three.
 */
export default function ImportDialog({
  open, onOpenChange, onImported, title, description, routes, templateName, initialOptions = {},
  formFields, summary, confirmLabel, successMessage, renderReport, guideFootnote,
}) {
  const [columns, setColumns] = useState(null);
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [refusal, setRefusal] = useState("");
  const [checking, setChecking] = useState(false);
  const [importing, setImporting] = useState(false);
  const [options, setOptions] = useState(initialOptions);
  const inputRef = useRef(null);
  const seq = useRef(0);

  useEffect(() => {
    if (!open || columns) return;
    api.get(routes.columns)
      .then((r) => Array.isArray(r.data) && setColumns(r.data))
      .catch(() => setColumns([]));
  }, [open, columns, routes.columns]);

  const reset = () => {
    seq.current++;
    setFile(null); setReport(null); setRefusal(""); setOptions(initialOptions);
    setChecking(false);
  };
  const close = (v) => { if (!v && !importing) { reset(); onOpenChange(false); } };

  const form = (f, opts) => {
    const fd = new FormData();
    fd.append("file", f);
    Object.entries(formFields(opts)).forEach(([k, v]) => fd.append(k, v ? "true" : "false"));
    return fd;
  };

  // Numbered: flipping a switch while the previous check is still out must not let the
  // older answer land over the newer one and show counts for the other setting.
  const check = async (f, opts) => {
    const n = ++seq.current;
    setChecking(true);
    try {
      const r = await api.post(routes.preview, form(f, opts));
      if (n !== seq.current) return;
      setReport(r.data); setRefusal("");
    } catch (e) {
      if (n !== seq.current) return;
      setReport(null);
      setRefusal(formatApiError(e.response?.data?.detail, "The file could not be checked."));
    } finally {
      if (n === seq.current) setChecking(false);
    }
  };

  const choose = (f) => {
    if (!f) return;
    reset();
    setFile(f);
    check(f, initialOptions);
  };

  const toggle = (next) => {
    const opts = { ...options, ...next };
    setOptions(opts);
    check(file, opts);
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.get(routes.template, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = templateName; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error("Could not download the template");
    }
  };

  const runImport = async () => {
    setImporting(true);
    try {
      const r = await api.post(routes.commit, form(file, options));
      toast.success(successMessage(r.data));
      reset();
      onOpenChange(false);
      onImported?.();
    } catch (e) {
      // Somebody else changed the register between the check and the import, most likely.
      // The refusal carries a fresh report, so the screen shows the new truth, not a toast.
      if (e.response?.data?.report) setReport(e.response.data.report);
      toast.error(formatApiError(e.response?.data?.detail, "Import failed"));
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-2xl max-h-[90dvh] overflow-y-auto" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <input ref={inputRef} type="file" accept=".xlsx" className="hidden" data-testid="import-file-input"
               onChange={(e) => { choose(e.target.files?.[0]); e.target.value = ""; }} />

        {!file ? (
          <div className="space-y-4 min-w-0">
            <DropZone onBrowse={() => inputRef.current?.click()} onDropFile={choose} />
            <ColumnGuide columns={columns} onTemplate={downloadTemplate} footnote={guideFootnote} />
          </div>
        ) : (
          <div className="space-y-4 min-w-0" data-testid="import-check">
            <div className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2">
              <div className="flex items-center gap-2 min-w-0">
                <FileSpreadsheet className="w-4 h-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{file.name}</div>
                  {report && <div className="text-xs text-muted-foreground">{summary(report)}</div>}
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => inputRef.current?.click()} disabled={importing}
                      data-testid="import-choose-another">Choose another</Button>
            </div>

            {checking && !report && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
                <Loader2 className="w-4 h-4 animate-spin" /> Reading the file…
              </div>
            )}

            {refusal && (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm" data-testid="import-refusal">
                <div className="font-medium text-destructive-text">This file cannot be imported as it is</div>
                <p className="mt-1">{refusal}</p>
                <Button variant="link" className="h-auto p-0 mt-2" onClick={downloadTemplate}>
                  <Download className="w-3.5 h-3.5 mr-1" /> Download the template
                </Button>
              </div>
            )}

            {report && renderReport({ report, options, busy: checking || importing, toggle })}
          </div>
        )}

        {/* Above the buttons, not inside the footer: the footer stacks in reverse on a phone,
            which put the reason Import is disabled underneath the button it explains. */}
        {report && !report.can_import && (
          <p className="text-sm text-muted-foreground sm:text-right -mb-2" data-testid="import-blocked">
            {report.blocked_by.join(". ")}.
          </p>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => close(false)} disabled={importing}>Cancel</Button>
          {file && (
            <Button onClick={runImport} disabled={!report?.can_import || checking || importing} data-testid="import-confirm">
              {importing && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              {report?.can_import ? confirmLabel(report) : "Import"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DropZone({ onBrowse, onDropFile }) {
  const [dragging, setDragging] = useState(false);
  return (
    <button type="button" onClick={onBrowse}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); onDropFile(e.dataTransfer.files?.[0]); }}
            className={`w-full flex flex-col items-center gap-2 rounded-md border-2 border-dashed p-8 transition-colors ${dragging ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50"}`}
            data-testid="import-drop">
      <Upload className="w-6 h-6 text-primary" />
      <span className="text-sm font-medium">Choose an .xlsx file, or drop it here</span>
      <span className="text-xs text-muted-foreground">Nothing is imported until you confirm</span>
    </button>
  );
}

function ColumnGuide({ columns, onTemplate, footnote }) {
  return (
    <div className="rounded-md border border-border overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-muted/40 border-b border-border">
        <span className="overline text-muted-foreground">Columns this import reads</span>
        <Button variant="link" size="sm" className="h-auto p-0" onClick={onTemplate} data-testid="download-template">
          <Download className="w-3.5 h-3.5 mr-1" /> Download template
        </Button>
      </div>
      {columns === null ? (
        <div className="p-3 text-xs text-muted-foreground">Loading…</div>
      ) : (
        <ul className="divide-y divide-border text-sm">
          {columns.map((c) => (
            <li key={c.key} className="px-3 py-1.5 grid grid-cols-[minmax(0,9rem)_minmax(0,1fr)] gap-x-3">
              <span className="font-medium">
                {c.label}{c.required && <span className="text-destructive-text ml-0.5" aria-label="required">*</span>}
              </span>
              <span className="text-muted-foreground min-w-0">
                <span className="font-mono text-xs">{c.example}</span>
                {c.note && <span className="block text-xs">{c.note}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className="px-3 py-1.5 text-xs text-muted-foreground border-t border-border">
        <span className="text-destructive-text">*</span> Required. Headers can be in any order and any
        case; {footnote}
      </p>
    </div>
  );
}

/** Which headers were read as what, second copies of a column, and what nothing reads. */
export function ColumnNotes({ report }) {
  const renamed = report.matched.filter((m) => m.header && m.header.toLowerCase() !== m.label.toLowerCase());
  return (
    <>
      {renamed.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Read {renamed.map((m) => `“${m.header}” as ${m.label}`).join(", ")}.
        </p>
      )}
      {report.duplicate_columns?.map((d) => (
        <p key={d.column} className="text-xs text-amber-700 dark:text-amber-400" data-testid="import-duplicate-column">
          Column {d.column} (“{d.header}”) is a second {d.label} column — only column {d.read} is read.
        </p>
      ))}
      {report.ignored_columns.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Not imported (nothing to put them in): {report.ignored_columns.join(", ")}.
        </p>
      )}
    </>
  );
}

export function ProblemList({ problems }) {
  if (!problems.length) return null;
  return (
    <div className="rounded-md border border-destructive/40" data-testid="import-problems">
      <div className="px-3 py-2 border-b border-destructive/30 text-sm font-medium text-destructive-text">
        Fix {problems.length === 1 ? "this row" : "these rows"} in the file, save it, and choose it again
      </div>
      <ul className="max-h-48 overflow-y-auto divide-y divide-border text-sm">
        {problems.map((p) => (
          <li key={p.row} className="px-3 py-1.5">
            <span className="font-mono text-xs text-muted-foreground mr-2">Row {p.row}</span>
            {p.label && <span className="font-medium mr-1">{p.label}:</span>}
            {p.issues.map((i) => i.message).join("; ")}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Count({ n, label, tone = "" }) {
  return (
    <div className="rounded-md border border-border px-3 py-2">
      <div className={`font-heading text-xl font-bold ${n ? tone : "text-muted-foreground"}`}>{n}</div>
      <div className="text-xs text-muted-foreground leading-tight">{label}</div>
    </div>
  );
}

export function Decision({ icon: Icon, title, body, control, controlLabel, footnote, testid }) {
  return (
    <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 space-y-2" data-testid={testid}>
      <div className="flex gap-2">
        <Icon className="w-4 h-4 mt-0.5 shrink-0 text-amber-700 dark:text-amber-400" />
        <div className="min-w-0 space-y-1">
          <div className="text-sm font-medium">{title}</div>
          <div className="text-sm text-muted-foreground">{body}</div>
        </div>
      </div>
      <label className="flex items-center gap-2 pl-6 text-sm font-medium cursor-pointer w-fit">
        {control} {controlLabel}
      </label>
      {footnote && <p className="pl-6 text-xs text-muted-foreground">{footnote}</p>}
    </div>
  );
}

export const quoteList = (values) => values.map((v) => `“${v}”`).join(", ");
